/**
 * **文件里的 `字,拼音` 词元**（2026-10-20 上传 .txt 那一轮）。
 *
 * ## 用户锁定的文件格式（纯文本 UTF-8，一行 = 课本上的一行）
 *
 * ```text
 * 霜,shuāng	吹,chuī	落,luò	降,jiàng	飘,piāo	游,yóu	池,chí	入,rù
 * 氏,shì	什,shén	李,lǐ	张,zhāng	弓,gōng	古,gǔ	胡,hú	吴,wú	言,yán	孙,sūn
 * ```
 *
 *   · **词元** = `汉字,拼音`（词语表则是 `词语,拼音`）；
 *   · 词元之间用 **Tab** 分隔（**也接受空格**）；
 *   · 允许 **BOM**、`\r\n`、空行（由 `parseDictationImport` 统一处理）；
 *   · 也允许某个词元**不写拼音**（`天`）。2026-10-20 起：**单音字**会被解析器
 *     自动补上它唯一的那一个读音（`autoReadingForChar()`，等于"已标音"）；
 *     **多音字**仍然留空（未标音，交给预览里的选音浮窗）——
 *     整个文件一个拼音都不写，就是"只写字"的格式（见 `fillAutoReadingForChar()`）。
 *
 * ## 「不确定」标记 `?`（2026-10-20 新增，用户定稿的格式）
 *
 * 生成文件的助手有时候也拿不准某个字读什么。用户要的是：**别让它猜**，
 * 而是**在文件里标出来**，由系统在预览里标黄、让他点一下确认：
 *
 * ```text
 * 得,dé?	背,bēi?	霜,shuāng
 * ```
 *
 *   · `字,拼音?` = **我不确定这个读音**（`?` 写在拼音末尾）；
 *   · **半角 `?` 与全角 `？` 都认**（全角会先被归一成半角，见下面的 `normalize`）；
 *   · `?` **不是拼音的一部分**：它在这里就被**剥掉**，`dé?` → `dé` 再交给
 *     `normalizePinyinSyllable()` —— 否则归一器会以「「?」不是拼音字母」拒掉整行；
 *   · 没有 `?` 的照旧（= 这个音是确定的）；
 *   · `字,?`（只有问号、没写拼音）也认：**读音留空 + 标记不确定**，
 *     等用户在预览里选一个音并确认；
 *   · ⚠️ 多音节拼音（词语表）的 `?` 只认**最后一个音节之后**那一个：
 *     `长江,cháng jiāng?` 整词标不确定；词语表**不参与**"待确认"拦截
 *     （那里没有"每个字一个位置"的确认入口，标了也只当格式标记剥掉）。
 *
 * ## 为什么不能直接用现有的 `splitCells()`（按逗号分列）
 *
 * 一行里每个词元**自己**就带一个逗号：`霜,shuāng	吹,chuī`。如果照"逗号是列分隔符"
 * 切，会得到 `霜` / `shuāng	吹` / `chuī` 这种碎片（实测会报"「shuāng 吹」不是汉字"）。
 * 所以这一层先**按空白切成段**，再把 `字,拼音` 认成**一个整体**。
 *
 * ## 空格 + 多音节拼音（词语表）怎么办
 *
 * `长江,cháng jiāng` 的拼音里**有空格**，而词元之间也可能用空格分隔 —— 单看空格
 * 分不清。这里的规则是**把后面"看起来是拼音"的段接上**（`长江,cháng` + `jiāng`）：
 *
 * ```text
 * 霜,shuāng 吹,chuī            ← 空格分隔，每个词元一个音节
 * 长江,cháng jiāng	黄河,huáng hé  ← Tab 分隔，词元里的拼音可以有空格
 * ```
 *
 * ## 为什么"认不出来就返回 null"（而不是尽量猜）
 *
 * 返回 `null` = **这一行不是本文格式**，交回既有的列式 / 词元行规则处理。
 * 这样 `天,地`（逗号分列的两个生字）、`天,1类字`（老的两列粘贴）都不会被这里
 * 抢走 —— 本项目最忌讳的就是"看起来像、其实猜错了"。
 *
 * 判据（三条同时成立才认）：
 *   1. 至少有一个词元带拼音；
 *   2. 每个段要么是 `汉字,拼音`（右边只含拼音字母 / 声调符号 / 数字 / 连字符），
 *      要么是**纯汉字**；
 *   3. 别的形状（含标点、空格以外的符号、右边是汉字）一律返回 `null`。
 */

/** 一行里认出来的一个词元。 */
export interface ReadingToken {
  /** 汉字（字表里是 1 个字；词语表里是 2 个及以上的词）。 */
  text: string;
  /**
   * 文件里写的拼音（**原样、已剥掉 `?` 标记**，还没校验/归一）；
   * `null` = 这个词元没写拼音。
   */
  pinyin: string | null;
  /**
   * 文件里在**这个拼音末尾**写了「?」（或全角「？」）= 生成文件的人
   * **不确定这个读音**，要用户在预览里确认。
   *
   * ⚠️ `?` **不构成拼音的一部分**：它在 `parsePair()` 里就被剥掉了，
   *    否则 `normalizePinyinSyllable('dé?')` 会以「「?」不是拼音字母」拒掉整行。
   * ⚠️ 没有 `?` = 这个音是确定的（老文件照旧，`uncertain: false`）。
   */
  uncertain: boolean;
}

/** 纯汉字（1..N 个）。 */
const HANZI_ONLY = /^[\u4e00-\u9fff]+$/;

/**
 * "看起来像拼音"的右边：拉丁字母 + 声调符号 + 数字（声调数字）+ 连字符 + `u:`。
 *
 * ⚠️ 刻意**不收汉字**：`天,地` 的右边是汉字，那不是拼音，必须交回列式规则。
 * ⚠️ 也刻意**不收空格**：空格在这里是"词元分隔符"，多音节拼音靠下面的合并逻辑
 *    拼回来。
 */
const PINYIN_LIKE = /^[A-Za-z0-9\u00e0-\u00ff\u0100-\u017f\u01cd-\u01dc\u1e00-\u1eff:'-]+$/;

/** `汉字,拼音` 的形状（逗号已经由调用方归一成半角）。 */
const PAIR_SHAPE = /^([\u4e00-\u9fff]+),([^\s,]+)$/;

/**
 * 剥掉末尾的「不确定」标记：**半角 `?` 与全角 `？` 都认**。
 *
 * ⚠️ 全角 `？`（U+FF1F）在上面的整行归一里已经变成 `?`，这里再判一次全角是为了
 *    让 `parsePair()` 单独被调用时也成立（口径只有一份）。
 * ⚠️ 只剥**末尾一个**：`dé??` 会剩一个 `?`，仍然过不了拼音校验 ⇒ 报错（不猜）。
 */
export function splitUncertainMark(raw: string): { text: string; uncertain: boolean } {
  if (raw.endsWith('?') || raw.endsWith('？')) {
    return { text: raw.slice(0, -1), uncertain: true };
  }
  return { text: raw, uncertain: false };
}

/**
 * 把一个段认成 `汉字,拼音`（`?` 已在**这一步**剥掉）；不是这个形状
 * （或右边不像拼音）返回 `null`。
 *
 * ⚠️ `字,?`（只有问号、没写拼音）**认**：拼音留空、`uncertain: true` ——
 *    生成文件的人可能连一个候选音都写不出来，那时让用户在预览里选。
 *    它**不是**"格式不认识"，所以不能再退回老路径（那会报一堆莫名其妙的错）。
 */
function parsePair(segment: string): { text: string; pinyin: string; uncertain: boolean } | null {
  const matched = PAIR_SHAPE.exec(segment);
  if (matched?.[1] === undefined || matched[2] === undefined) return null;
  const mark = splitUncertainMark(matched[2]);
  if (mark.text === '') {
    /** 只有 `?`：拼音留空 + 标记不确定；只有 `,` 没有 `?` 则不是本文格式。 */
    return mark.uncertain ? { text: matched[1], pinyin: '', uncertain: true } : null;
  }
  if (!PINYIN_LIKE.test(mark.text)) return null;
  return { text: matched[1], pinyin: mark.text, uncertain: mark.uncertain };
}

/**
 * 把一行拆成**带拼音的词元**。
 *
 * @param line 原文一行（**可以含 Tab / 空格 / 全角空格 / BOM**）。
 * @returns 认出来的词元；`null` = 这一行不是"`字,拼音` 词元行"，请按既有规则处理。
 */
export function splitReadingTokens(line: string): ReadingToken[] | null {
  /**
   * ⚠️ 先做与 `normalizeCell` 同口径的归一（全角逗号/空格 → 半角、去 BOM/零宽），
   *    否则从 Excel / 记事本粘出来的全角逗号会认不出来。
   */
  const normalized = line
    .replace(/[\uFEFF\u200B-\u200D]/g, '')
    .replace(/[\uFF01-\uFF5E]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/\u3000/g, ' ');
  const segments = normalized.split(/[\s]+/).filter((segment) => segment !== '');

  const tokens: ReadingToken[] = [];
  /** 认出了几个 `字,拼音` 词元（含 `字,?`：它也是**本文格式**，只是读音没写）。 */
  let pairCount = 0;
  let index = 0;

  while (index < segments.length) {
    const segment = segments[index] ?? '';
    const pair = parsePair(segment);
    if (pair !== null) {
      let pinyin = pair.pinyin;
      /** 不确定标记可能写在**多音节拼音的最后一个音节**后面（`cháng jiāng?`）。 */
      let uncertain = pair.uncertain;
      index += 1;
      /**
       * 多音节拼音被空格切开了（`长江,cháng` + `jiāng`）：把后面**看起来是拼音**的段
       * 补回来。于是下面两种写法都认：
       *
       *     长江,cháng jiāng	黄河,huáng hé   ← Tab 分隔，拼音里有空格
       *     霜,shuāng 吹,chuī                  ← 空格分隔，每个词元一个音节
       *
       * ⚠️ 下一个段如果**自己就是一个 `字,拼音`**，绝不能并进来（那是下一个词元）。
       * ⚠️ 并多了也没关系：`霜,shuāng1 jiang1` 会被当成"一个汉字写了两个音节"报错，
       *    比退回老路径报「词元「霜,shuang1」是 8 个字符」清楚得多（宁可多并、不猜）。
       * ⚠️ 并入的段也可能带 `?`（`长江,cháng jiāng?`）：标记并进这个词元，
       *    音节本身**不带** `?`（`?` 绝不进拼音）。
       */
      while (index < segments.length) {
        const next = segments[index] ?? '';
        if (parsePair(next) !== null) break;
        const bare = splitUncertainMark(next);
        if (bare.text === '' || !PINYIN_LIKE.test(bare.text)) break;
        pinyin = pinyin === '' ? bare.text : `${pinyin} ${bare.text}`;
        if (bare.uncertain) uncertain = true;
        index += 1;
      }
      tokens.push({
        text: pair.text,
        /** `字,?` 的拼音是空串 ⇒ 交回"没写拼音"那条路（自动读音 / 留空）。 */
        pinyin: pinyin === '' ? null : pinyin,
        uncertain,
      });
      pairCount += 1;
      continue;
    }
    if (HANZI_ONLY.test(segment)) {
      tokens.push({ text: segment, pinyin: null, uncertain: false });
      index += 1;
      continue;
    }
    /** 有别的形状（标点、字母……）⇒ 这不是本文格式，整行交回既有规则。 */
    return null;
  }

  if (pairCount === 0) return null;
  return tokens;
}
